<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, useId } from "vue";
import LucideIcon from "../../components/icons/LucideIcon.vue";
import "./SuggestedOrdersColumnPicker.css";

const props = defineProps<{
  columns: readonly string[];
  visibleColumns: readonly string[];
}>();
const emit = defineEmits<{
  change: [columns: string[]];
  reset: [];
}>();
const dialogId = useId();
const open = ref(false);
const root = ref<HTMLElement>();
const trigger = ref<HTMLButtonElement>();
const dialog = ref<HTMLElement>();
const dialogPosition = ref({ top: "-9999px", left: "-9999px" });

function positionDialog(): void {
  if (!open.value || !trigger.value || !dialog.value) return;
  const anchor = trigger.value.getBoundingClientRect();
  const panel = dialog.value.getBoundingClientRect();
  const margin = 8;
  dialogPosition.value = {
    top: `${String(Math.max(margin, Math.min(anchor.bottom + 4, window.innerHeight - panel.height - margin)))}px`,
    left: `${String(Math.max(margin, Math.min(anchor.right - panel.width, window.innerWidth - panel.width - margin)))}px`,
  };
}

function containsTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Node &&
    (root.value?.contains(target) === true ||
      dialog.value?.contains(target) === true)
  );
}

function close(restoreFocus = false): void {
  open.value = false;
  if (restoreFocus) trigger.value?.focus();
}

async function toggle(): Promise<void> {
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    positionDialog();
    dialog.value?.focus();
  }
}

function toggleColumn(column: string, event: Event): void {
  if (!(event.target instanceof HTMLInputElement)) return;
  const selected = new Set(props.visibleColumns);
  if (event.target.checked) selected.add(column);
  else selected.delete(column);
  emit(
    "change",
    props.columns.filter((name) => selected.has(name))
  );
}

function dismissOutside(event: PointerEvent): void {
  if (!containsTarget(event.target)) close();
}

function onDocumentKeydown(event: KeyboardEvent): void {
  if (
    open.value &&
    event.key === "Tab" &&
    event.target instanceof Node &&
    dialog.value?.contains(event.target)
  ) {
    onDialogTab(event);
  }
  if (open.value && event.key === "Escape" && containsTarget(event.target)) {
    event.preventDefault();
    event.stopPropagation();
    close(true);
  }
}

function dismissOnFocusout(event: FocusEvent): void {
  if (!containsTarget(event.relatedTarget)) close();
}

function onDialogTab(event: KeyboardEvent): void {
  const controls = [
    ...(dialog.value?.querySelectorAll<HTMLElement>(
      "input:not(:disabled), button"
    ) ?? []),
  ];
  if (controls.length === 0) return;
  if (
    event.shiftKey &&
    (event.target === dialog.value || event.target === controls[0])
  ) {
    event.preventDefault();
    close(true);
  } else if (!event.shiftKey && event.target === controls.at(-1)) {
    // Resume the page's natural tab order after the owning trigger.
    close(true);
  }
}

onMounted(() => {
  document.addEventListener("pointerdown", dismissOutside);
  document.addEventListener("keydown", onDocumentKeydown);
  window.addEventListener("resize", positionDialog);
  window.addEventListener("scroll", positionDialog, true);
});
onUnmounted(() => {
  document.removeEventListener("pointerdown", dismissOutside);
  document.removeEventListener("keydown", onDocumentKeydown);
  window.removeEventListener("resize", positionDialog);
  window.removeEventListener("scroll", positionDialog, true);
});
</script>

<template>
  <div
    ref="root"
    class="suggested-orders-column-picker"
    role="group"
    aria-label="Results column controls"
    @focusout="dismissOnFocusout"
  >
    <button
      ref="trigger"
      type="button"
      class="suggested-orders-columns-trigger"
      aria-label="Choose Results columns"
      title="Choose Results columns"
      aria-haspopup="dialog"
      :aria-expanded="open"
      :aria-controls="open ? dialogId : undefined"
      @click="toggle"
      @keydown.down.prevent.stop="!open && toggle()"
    >
      <LucideIcon name="settings" :size="18" />
    </button>
    <Teleport to="body">
      <div
        v-if="open"
        :id="dialogId"
        ref="dialog"
        role="dialog"
        aria-label="Results columns"
        tabindex="-1"
        class="suggested-orders-columns-dialog"
        :style="dialogPosition"
        @focusout="dismissOnFocusout"
      >
        <p>Results columns</p>
        <div class="suggested-orders-column-options">
          <label
            v-for="(column, index) in columns"
            :key="column"
            :for="`${dialogId}-${String(index)}`"
          >
            <input
              :id="`${dialogId}-${String(index)}`"
              type="checkbox"
              :checked="visibleColumns.includes(column)"
              :disabled="
                visibleColumns.length === 1 && visibleColumns.includes(column)
              "
              @change="toggleColumn(column, $event)"
            />
            {{ column }}
          </label>
        </div>
        <button type="button" @click="emit('reset')">Reset to defaults</button>
        <button type="button" @click="close(true)">Close columns</button>
      </div>
    </Teleport>
  </div>
</template>
